import React, { useMemo, useState } from 'react';
import { Input, Row, Col, Typography, Divider, Space, Spin, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined, DeploymentUnitOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import type { QaqcPipeWorkarea } from '../../../../types';
import { useGetQaqcPipeWorkarea } from '../../../../hooks/genericHooks';
import StandardTable from '../../../../components/common/StandardTable';
import { useTablePagination } from '../../../../components/common/useTablePagination';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

const QaqcPipeWorkareaList: React.FC = () => {
  const navigate = useNavigate();

  // Filters
  const [nameSearch, setNameSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState<string | undefined>();

  // Data
  const { data: resp, isLoading } = useGetQaqcPipeWorkarea();
  const rows: QaqcPipeWorkarea[] = useMemo(() => (Array.isArray((resp as any)?.data) ? (resp as any).data : ((Array.isArray(resp) ? (resp as any) : []))) as QaqcPipeWorkarea[], [resp]);

  // Pagination
  const { getPaginationProps } = useTablePagination(15);

  // Name-level filtering (drives cascading options for sections)
  const nameFilteredRows = useMemo(() => {
    const nameQ = nameSearch.trim().toLowerCase();
    if (!nameQ) return rows;
    return rows.filter(r => r.name.toLowerCase().includes(nameQ));
  }, [rows, nameSearch]);

  // Section options cascade from the name-filtered set
  const sectionOptions = useMemo(() => {
    const sections = new Set<string>();
    nameFilteredRows.forEach(r => (r.section_list || []).forEach(s => s && sections.add(s)));
    return [{ label: 'All Sections', value: '' }, ...Array.from(sections).sort().map(s => ({ label: s, value: s }))];
  }, [nameFilteredRows]);

  // Final filtered data: apply section filter on top of name filter
  const filteredData = useMemo(() => {
    return nameFilteredRows.filter(r => !sectionFilter || (r.section_list || []).includes(sectionFilter));
  }, [nameFilteredRows, sectionFilter]);

  // Actions
  const handleCreate = () => navigate('/qaqc/piping/tools/workareas/create');
  const handleView = (id: number) => navigate(`/qaqc/piping/tools/workareas/view/${id}`);
  const handleEdit = (id: number) => navigate(`/qaqc/piping/tools/workareas/edit/${id}`);

  // Columns
  const columns: ColumnsType<QaqcPipeWorkarea> = [
    { title: 'Area Name', dataIndex: 'name', key: 'name', width: 40, sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Sections', dataIndex: 'section_list', key: 'section_list',width: 360, render: (v: string[] | null) => (v && v.length ? v.join(', ') : ''), ellipsis: true },
    {
      title: 'Action', key: 'action', align: 'center', width: 30,
      render: (_, record) => (
        <Space>
          <Button action="read" icon={<EyeOutlined />} onClick={() => handleView(record.id)} />
          <Button action="edit" type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
        </Space>
      )
    },
  ];

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <DeploymentUnitOutlined style={{ marginRight: 8 }} /> QAQC Pipe Work Areas
            </Title>
          </Col>
          <Col>
            <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Work Area
            </Button>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              allowClear
              placeholder="Search by name"
              prefix={<SearchOutlined />}
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              allowClear
              showSearch
              placeholder="Filter by section"
              value={sectionFilter}
              onChange={(val) => setSectionFilter(val || undefined)}
              options={sectionOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredData}
            rowKey="id"
            loading={isLoading}
            pagination={getPaginationProps(filteredData.length)}
            scroll={{ x: true }}
            bordered
            size="small"
          />
        </Spin>
      </div>
    </>
  );
};

export default QaqcPipeWorkareaList;
